import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flowRight } from 'lodash';
import { GRID_NUMBER_CHECKBOX, LEDGER, NUMBER, PERCENTAGE } from 'common/constants/gridType';
import { GET_ENTER_DATA } from 'common/constants/inputs';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import AlternateCurrencyChip
  from 'components/ScalarSpreadsheet/utilities/alternate-currency-chip/AlternateCurrencyChip';
import withAction from 'components/WithAction/withAction';
import withChip from 'components/WithChip/withChip';
import withDialog from 'components/WithDialog/withDialog';
import withLink from 'components/WithLink/withLink';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { formatNumbers, toString } from 'utilities';

interface FastValueViewerProps {
  value?: any;
  cell: ICell;
  selected?: boolean;
  onDoubleClick?: (event: React.MouseEvent) => void;
  setDoubleClicked?: (value: boolean) => void;
}

const FastValueViewer: React.FC<FastValueViewerProps> = ({
  cell,
  selected,
  onDoubleClick,
  setDoubleClicked,
  value: cellValue,
}) => {
  const { tooltipRef, width } = useRevealEllipsisInTooltip();

  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || event.keyCode;
      if (selected && key && key.toString().toUpperCase() === 'F2') {
        //  We used this two functions and setters together because we wanted to adapt our
        //  code to work as the "new double click", a bunch of setters made by Jordan in ValueEditor
        //  to preserve the last value that was before the double click
        setDoubleClicked?.(true);
        onDoubleClick?.(event as unknown as React.MouseEvent);
      }
    },
    [onDoubleClick, selected, setDoubleClicked]
  );

  const { format } = useContext(FeaturedSpreadsheetContext);

  const cellFormat = useMemo(() => {
    if (format && format !== cell.format) {
      if (cell.currencySpecification && format.currency.additionalCurrencies) {
        // find the relevant currency format
        const additionalCurrency = format.currency.additionalCurrencies.find(
          additionalCurrency => additionalCurrency.currencyType === cell.currencySpecification
        );
        return {
          ...format,
          currency: {
            ...format.currency,
            sourceCurrency: additionalCurrency?.sourceCurrency,
            symbol: additionalCurrency?.symbol,
            exchangeRate: additionalCurrency?.exchangeRate,
          },
        };
      }
      return format;
    }
    return {};
  }, [cell, format]);

  const gridTypeHandlers = useMemo(
    () => [
      {
        // eslint-disable-next-line no-restricted-globals
        canHandle: (currentCell: ICell) =>
          [NUMBER, PERCENTAGE, GRID_NUMBER_CHECKBOX].includes(currentCell.gridType as string) &&
          !isNaN(Number(currentCell.value)),
        getValue: (currentCell: ICell) => {
          if (!currentCell.readOnly && !toString(currentCell.value).length) {
            return currentCell.enterDataMessage ?? GET_ENTER_DATA(currentCell.isRequired, currentCell.enterDataMessage);
          }
          if (currentCell.readOnly && currentCell.allowEmptyValue && !toString(currentCell.value).length) {
            return '';
          }
          const mergedCurrencyFormat = {
            currency: {
              ...cellFormat?.currency,
              ...currentCell.customFormat?.currency,
            },
          };
          return formatNumbers({
            ...currentCell,
            ...cellFormat,
            ...currentCell.customFormat,
            ...mergedCurrencyFormat,
          });
        },
      },
      {
        canHandle: (currentCell: ICell) => currentCell.gridType === LEDGER,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        getValue: (currentCell: ICell) => 0,
      },
    ],
    [cellFormat]
  );

  const calcValue = useCallback(() => {
    if (cell.expr === '=') {
      return '=';
    }

    if (cell.hidden) {
      return '';
    }

    const handler = gridTypeHandlers.find(h => h.canHandle(cell));
    if (handler) {
      return handler.getValue(cell);
    }
    if (!cell.readOnly && !cell.hidden && !cell.value && cell.placeholder) {
      return cell.placeholder;
    }
    return cell.value;
  }, [cell, gridTypeHandlers]);

  const [value, setValue] = useState<any>();

  const getValue = useCallback(() => {
    setValue(calcValue());
  }, [calcValue]);

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [keyUpHandler]);

  useEffect(() => {
    getValue();
  }, [cell.value, cell.currency, cell.suffix, cell.hidden, getValue]);

  const spanId = useMemo(() => {
    if (cell.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }

    return `${cell.alias}-${cell.key}_viewer`;
  }, [cell]);
  const valueOrNA = useMemo(() => (cell.value !== null ? value : 'N/A'), [cell, value]);

  if (
    cell.className?.includes('table-header') ||
    cell.className?.includes('header-no-border') ||
    cell.className?.includes('show-tooltip')
  ) {
    return (
      <span className="value-viewer" ref={tooltipRef}>
        {value ? (
          <RowLabelTooltip title={value} containerWidth={width}>
            <span id={`${cell.alias}-${cell.key}`} className="text-cell">
              {value}
            </span>
          </RowLabelTooltip>
        ) : (
          <span id={`${cell.alias}-${cell.key}`} className="text-cell">
            {value}
          </span>
        )}
      </span>
    );
  }

  if (cell.currencySpecification && format.currency.additionalCurrencies) {
    return (
      <AlternateCurrencyChip
        value={!cell.displayNAforNull ? value : valueOrNA}
        exchangeRate={cellFormat.currency.exchangeRate}
        spanId={spanId}
        className={cell.className}
        baseCurrency={format.currency.code}
        sourceCurrency={cellFormat.currency.sourceCurrency}
      />
    );
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {!cell.displayNAforNull ? value : valueOrNA}
      </span>
    </span>
  );
};

// flowRight is a compose method alternative
const enhance = flowRight(withTooltip, withDialog, withChip, withRequired, withLink, withAction);

export default enhance(FastValueViewer);